/**
 * Maps a mediasoup producer to a video-pipeline slot index. Nothing to do with peer
 * identity (see `peer-id.ts` for that).
 */

import type { NewConsumerData } from "./protoo-messages";

/** Fallback grid when robot-config catalog has not arrived yet (join vs get-capabilities race). */
const LEGACY_MOUNT_SLOTS: Record<string, number> = Object.freeze({
  front: 0,
  rear: 1,
  left: 2,
  right: 3,
});

function slotFromLegacyMount(mount: unknown): number | null {
  const key = String(mount || "")
    .trim()
    .toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(LEGACY_MOUNT_SLOTS, key)) return null;
  return LEGACY_MOUNT_SLOTS[key] ?? null;
}

export type ResolveMountSlot = (mount: string) => number | null;

/**
 * Mount→slot resolver (robot-config / camera-catalog driven), injected per call
 * instead of held as module state — the package itself knows nothing about robots or
 * camera catalogs, and each session passes its own resolver (see
 * `createTeleopSession`'s `resolveMountSlot` option, threaded through
 * `createRoomConsumers` → `resolveConsumerSlot`). Two independent sessions in the
 * same tab never share or clobber each other's resolver this way. When `resolve` is
 * null/omitted, only the legacy fallback grid is used.
 */
function slotForMount(mount: string, resolve?: ResolveMountSlot | null): number | null {
  if (resolve) {
    const fromResolver = resolve(mount);
    if (fromResolver != null) return fromResolver;
  }
  return slotFromLegacyMount(mount);
}

/**
 * Signaling id is `{prefix}-{mount}-{label}{suffix}` (e.g. dog03-left-b2-left6t205dhr).
 * Take the first hyphen-separated token that is a known mount — not a substring scan
 * (label `b2-front` must not steal a left camera).
 */
function mountFromPeerId(pid: unknown): string | null {
  const s = String(pid || "");
  if (!s || /^operator-/i.test(s)) return null;
  for (const part of s.split("-")) {
    const key = part.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(LEGACY_MOUNT_SLOTS, key)) return key;
  }
  return null;
}

function slotFromProducerPeerId(pid: unknown, resolve?: ResolveMountSlot | null): number | null {
  const mount = mountFromPeerId(pid);
  if (!mount) return null;
  return slotForMount(mount, resolve);
}

/**
 * Map mediasoup producer → video-pipeline slot index.
 *
 * `d.appData.uiSlot`/`ui_slot` here is the SFU's own wire field name (set on the
 * producer's appData) — kept as-is because it is an external contract this package
 * does not own, NOT because this transport module has an opinion about UI layout.
 * The app-level camera catalog (`teleop-state`) deliberately calls the equivalent
 * concept `channel` instead, for exactly that reason — see `CameraCatalogEntry.channel`
 * in `modules/teleop-state/camera-catalog.ts`. Both end up resolving to the same
 * number: the index this module uses for its internal per-slot bookkeeping
 * (`streams[slot]` etc. in `media/video-pipeline.ts`), needed before any UI renders.
 *
 * @param resolveMountSlot per-session mount→slot resolver (see `slotForMount` doc) —
 *   null/omitted uses only the legacy fallback grid.
 */
export function resolveConsumerSlot(
  d: NewConsumerData,
  producerSlot: Map<string, number>,
  log: (s: string) => void,
  resolveMountSlot?: ResolveMountSlot | null
): number {
  const producerId = d.producerId;
  const cached = producerSlot.get(producerId);
  if (cached !== undefined) return cached;

  const app = d.appData || d.producerAppData || {};
  // SFU wire field (unrelated to teleop-state's `channel` naming, see doc above).
  const rawSlot = app.uiSlot ?? app.ui_slot;
  if (rawSlot != null && `${String(rawSlot)}` !== "") {
    const n = Number(rawSlot);
    if (Number.isFinite(n) && n >= 0) {
      const k = Math.floor(n);
      producerSlot.set(producerId, k);
      return k;
    }
  }

  const appMount = String(app.cameraMount || app.camera || "")
    .trim()
    .toLowerCase();
  if (appMount) {
    const fromApp = slotForMount(appMount, resolveMountSlot);
    if (fromApp != null) {
      producerSlot.set(producerId, fromApp);
      return fromApp;
    }
  }

  const idCandidates = [
    d.producerPeerId,
    d.ownerPeerId,
    d.sourcePeerId,
    d.producerPeer?.id,
    d.peerId,
    d.peer,
    app.peerId,
  ]
    .map((x) => (x == null ? "" : String(x)))
    .filter(Boolean);
  for (const c of idCandidates) {
    const fromPeer = slotFromProducerPeerId(c, resolveMountSlot);
    if (fromPeer !== null) {
      producerSlot.set(producerId, fromPeer);
      return fromPeer;
    }
  }

  log(
    `resolveConsumerSlot: unknown producerId=${producerId}, default 0. keys=${Object.keys(d || {}).join(",")}`
  );
  producerSlot.set(producerId, 0);
  return 0;
}
